import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Flex, Form, GetProp, notification, UploadProps } from 'antd'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  applySkipRowsRange,
  applySkipRowsRangeToText,
  defaultValues,
  EXTRA_COLUMN,
  findFirstHeaderRowIndex,
  formatSheetData,
  generateValidationSchema,
  getKeptColumnIndicesForRawData,
  LIMIT_FILE_SIZE,
  preprocessSheetLikeDataForPreview,
} from './utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import * as XLSX from 'xlsx'
import { pickFirstViableExcelSheet } from '../pickFirstViableExcelSheet'
import { checkDuplicateFileMappingFormats, createFileMappingFormats } from 'src/app/shared/services/ClientSetupService'
import { useNavigate } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { FILE_TYPE_MAPPING } from 'src/app/shared/constants/app.constants'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import { Value } from 'src/app/shared/models/lookupValues.model'

import MappingFields from './MappingFields'
import UploadForm from './UploadForm'
import './index.scss'
import { csvHelper } from '../csvHelper'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import MappingFieldsTxt from './MappingFieldsTxt'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]
interface Props {
  children: React.ReactNode
  setFileContainsHeaderRow: (value: any) => void
  setFileData: (value: any) => void
  setColumnDefs: (value: any) => void
  setIsTxtFile: (value: any) => void
  setLoading: (value: any) => void
}

function AddFileMappingWrapper(props: Readonly<Props>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const lookupValues = useSelector(getLookupValues)

  const { FormatType } = lookupValues || {}

  const [file, setFile] = useState<any>()
  const [fileData, setFileData] = useState<any>()
  const [columnList, setColumnList] = useState<any>()
  const [isLoading, setIsLoading] = useState(false)
  const btnSaveRef = useRef<any>(null)
  const detectedHeaderRowSkipRef = useRef(0)
  // Maps a sequential position in the filtered fileData (M kept columns) back to the original
  // 0-based column index in the uploaded file. Used so dropdown values, columnDefs.colId and
  // saved mapping indices all point to the real column in the actual issued-check file.
  const keptColumnIndicesRef = useRef<number[]>([])
  const [viewNewUploadError, setViewNewUploadError] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<any>({
    checkNumber: null,
    amount: null,
    issuedDate: null,
    accountNumber: null,
    issuedPayee: null,
    recordType: null,
  })

  const {
    control,
    formState: { errors, touchedFields, dirtyFields },
    setError,
    clearErrors,
    setValue,
    resetField,
    setFocus,
    getValues,
    handleSubmit,
    trigger,
  } = useForm<any>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(generateValidationSchema(t, 1, fileData?.[0]?.length)),
  })

  const formatType = useWatch({
    control,
    name: 'formatType',
  })

  const fileContainsIssuedDate = useWatch({
    control,
    name: 'fileContainsIssuedDate',
  })

  const fileContainsHeaderRow = useWatch({
    control,
    name: 'fileContainsHeaderRow',
  })

  const textQualifier = useWatch({
    control,
    name: 'textQualifier',
  })

  const dateInFileDontIncludeSeparators = useWatch({
    control,
    name: 'dateInFileDontIncludeSeparators',
  })

  const isTxt = useMemo(() => formatType === 'txt', [formatType])
  const isCsvFile = useMemo(() => formatType === 'csv', [formatType])

  const readUploadFileData = (file: FileType) => {
    const reader = new FileReader()

    reader.onload = (e: any) => {
      let converted = e.target.result
      let headerColumns: any = []
      if (isCsvFile || isTxt) {
        // US32690 - the Row Skip Range is applied to the raw file text, before the existing parsing
        // and preamble detection, so the remaining rows keep going through the current pipeline.
        converted = applySkipRowsRangeToText(converted, getValues('skipRowsFrom'), getValues('skipRowsTo'))
        if (isCsvFile && csvHelper(converted, getValues('textQualifier')) === false) {
          setError('sampleFile', {
            message: t(`SystemMessages:${getValues('textQualifier') == 'SINGLEQUOTE' ? 'PPC09' : 'PPC10'}`),
          })
          setFile(null)
          setViewNewUploadError(true)
          props?.setLoading(false)
          return false
        } else {
          converted = isCsvFile
            ? csvHelper(converted, getValues('textQualifier'))
            : converted.split(/\r\n|\n|\r/gm).map((item: any) => item.split(''))

          if (isCsvFile) {
            detectedHeaderRowSkipRef.current = findFirstHeaderRowIndex(converted as unknown[][], fileContainsHeaderRow)
            keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
              converted as unknown[][],
              fileContainsHeaderRow,
              false,
            )
            converted = preprocessSheetLikeDataForPreview(converted, fileContainsHeaderRow, undefined, false)
          } else {
            detectedHeaderRowSkipRef.current = 0
            keptColumnIndicesRef.current = []
          }

          const dataSkipped =
            0 < converted?.length
              ? converted?.[0]
              : []

          const maxLength = Math.max(...converted.map((row: any) => row.length))
          isTxt && dataSkipped.push(...Array(EXTRA_COLUMN + maxLength - dataSkipped?.length).fill(''))

          headerColumns = dataSkipped.map((item: any, index: any) =>
            isTxt ? index + 1 : item.trim().replace(/[\n\r]*/g, ''),
          )

          const columnList = headerColumns?.map((item: any, seqIdx: any) => {
            const originalIdx = isTxt ? seqIdx : keptColumnIndicesRef.current[seqIdx] ?? seqIdx
            return {
              value: originalIdx,
              label: fileContainsHeaderRow
                ? String(item)?.includes('_blank_')
                  ? ``
                  : `${isTxt ? originalIdx + 1 : item}`
                : `${isTxt ? originalIdx + 1 : `Column ${originalIdx + 1}`}`,
            }
          })

          if (headerColumns) {
            setColumnList(columnList)
          }
          const rowData = converted?.slice(1)

          const newRowData: any = []
          rowData.forEach((row: any) => {
            let newRow = {}
            row.forEach((cell: any, cellIndex: number) => {
              if (cell === '' && cellIndex === 0) {
                row[cellIndex] = ''
              } else {
                newRow = {
                  ...newRow,
                  [headerColumns[cellIndex]]: cell,
                }
              }
            })

            newRowData.push(newRow)
          })
        }

        converted = converted?.slice(
          0,
          converted?.length,
        )

        if (isTxt && fileContainsHeaderRow) {
          converted.unshift(headerColumns)
        }

        setFileData(converted)

        props?.setLoading(false)
        return false
      }

      const binaryStr = e.target.result

      const workbook = XLSX.read(binaryStr, {
        type: 'binary',
        raw: isCsvFile,
        cellDates: !isCsvFile,
      })

      const { worksheet } = pickFirstViableExcelSheet(workbook, { raw: isCsvFile })

      // Convert sheet data to JSON
      const listData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        blankrows: true,
        range: 0,
        defval: '',
        raw: isCsvFile,
      })

      const sheetData: any = applySkipRowsRange(
        listData as unknown[][],
        getValues('skipRowsFrom'),
        getValues('skipRowsTo'),
      )
      detectedHeaderRowSkipRef.current = findFirstHeaderRowIndex(sheetData as unknown[][], fileContainsHeaderRow)
      keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
        sheetData as unknown[][],
        fileContainsHeaderRow,
        false,
      )
      const formattedSheetData = formatSheetData(sheetData, fileContainsHeaderRow)

      setFileData(formattedSheetData)

      const columnList = formattedSheetData?.[0]?.map((item: any, seqIdx: any) => {
        const originalIdx = keptColumnIndicesRef.current[seqIdx] ?? seqIdx
        return {
          value: originalIdx,
          label: fileContainsHeaderRow
            ? String(item)?.includes('_blank_')
              ? ``
              : item
            : `Column ${originalIdx + 1}`,
        }
      })

      setColumnList(columnList)
    }

    if (isTxt || isCsvFile) {
      reader.readAsText(file, 'UTF-8')
    } else {
      reader.readAsBinaryString(file)
    }
    reader.onloadend = () => {
      props?.setLoading(false)
    }
  }

  const getCurrentFormatType = (formatType: string) => {
    return FormatType?.find((formatItem: Value) => formatItem.value === formatType)?.label
  }

  const beforeUpload = (file: FileType) => {
    // return false to prevent default action ( call upload api ) of antd upload
    props?.setLoading(true)
    clearErrors('sampleFile')
    setViewNewUploadError(false)
    if (!formatType) {
      setError('sampleFile', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SELECT_FORMAT_TYPE_FIRST) })
      setViewNewUploadError(true)
      return false
    }
    if (file?.size > LIMIT_FILE_SIZE) {
      setError('sampleFile', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.EXCEED_LIMIT_FILE_SIZE) })
      setViewNewUploadError(true)
      return false
    }

    const fileType = file?.type
    const selectedFormatType = formatType as string
    if (!FILE_TYPE_MAPPING[selectedFormatType].includes(fileType)) {
      setError('sampleFile', {
        message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.INVALID_FILE_FORMAT, {
          Format_Type: getCurrentFormatType(selectedFormatType),
        }),
      })
      setViewNewUploadError(true)
      return false
    }

    // UPLOAD VALID FILE
    resetMappingFields()
    setFile(file)
    setValue('sampleFile', file?.name)

    readUploadFileData(file)

    return false
  }

  useEffect(() => {
    setFocus('formatName')
  }, [setFocus])

  useEffect(() => {
    resetField('issuedDateFormat')
  }, [dateInFileDontIncludeSeparators, resetField])

  const resetMappingFields = useCallback(() => {
    setSelectedColumns({
      checkNumber: null,
      amount: null,
      issuedDate: null,
      accountNumber: null,
      issuedPayee: null,
      recordType: null,
    })
    resetField('checkNumber')
    resetField('amount')
    resetField('issuedDate')
    resetField('dateInFileDontIncludeSeparators')
    resetField('issuedDateFormat')
    resetField('accountNumber')
    resetField('issuedPayee')
    resetField('recordType')

    resetField('checkNumberFrom')
    resetField('checkNumberTo')
    resetField('amountFrom')
    resetField('amountTo')
    resetField('issuedDateFrom')
    resetField('issuedDateTo')
    resetField('accountNumberFrom')
    resetField('accountNumberTo')
    resetField('issuedPayeeFrom')
    resetField('issuedPayeeTo')
    resetField('recordTypeFrom')
    resetField('recordTypeTo')
  }, [resetField])

  const columnDefs = useMemo(() => {
    const resolveOriginalIdx = (seqIdx: number) =>
      isTxt ? seqIdx : keptColumnIndicesRef.current[seqIdx] ?? seqIdx

    if (fileContainsHeaderRow && fileData) {
      return fileData?.[0]?.map((item: any, seqIdx: any) => {
        const originalIdx = resolveOriginalIdx(seqIdx)
        return {
          colId: originalIdx,
          field: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
          headerName: String(item)?.includes('_blank_') ? '' : String(item),
          minWidth: 40,
          sortable: false,
        }
      })
    }

    return fileData?.[0]?.map((item: any, seqIdx: any) => {
      const originalIdx = resolveOriginalIdx(seqIdx)
      return {
        colId: originalIdx,
        field: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
        headerName: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
        minWidth: 40,
        sortable: false,
      }
    })
  }, [fileContainsHeaderRow, fileData])

  useEffect(() => {
    props.setColumnDefs([])
    props.setFileData([])
    props.setFileContainsHeaderRow([])
    props.setIsTxtFile(isTxt)
  }, [isTxt])

  useEffect(() => {
    props.setColumnDefs(columnDefs)
    props.setFileData(fileData)
    props.setFileContainsHeaderRow(fileContainsHeaderRow)
  }, [fileContainsHeaderRow, fileData, columnDefs])

  // US32690 - the Row Skip Range changes which rows reach the preview and the column list,
  // so the already selected sample file is read again instead of being cleared.
  const handleSkipRowsChange = useCallback(() => {
    if (!file?.name) return

    props?.setLoading(true)
    resetMappingFields()
    readUploadFileData(file)
  }, [file, resetMappingFields])

  const resetUploadFile = useCallback(() => {
    detectedHeaderRowSkipRef.current = 0
    setColumnList([])
    setFile({})
    resetField('sampleFile')
    resetMappingFields()
  }, [resetField, setFile, resetMappingFields])

  useEffect(() => {
    if (
      formatType ||
      fileContainsHeaderRow ||
      fileContainsIssuedDate ||
      textQualifier
    ) {
      resetUploadFile()
    }
  }, [
    formatType,
    resetUploadFile,
    fileContainsHeaderRow,
    fileContainsIssuedDate,
    textQualifier,
  ])

  const requiredFields = !isTxt
    ? getValues('checkNumber') == null ||
      getValues('amount') == null ||
      (fileContainsIssuedDate && getValues('issuedDate') == null) ||
      (fileContainsIssuedDate && getValues('issuedDateFormat') === null)
    : getValues('checkNumberFrom') == null ||
      getValues('checkNumberTo') == null ||
      getValues('amountFrom') == null ||
      getValues('amountTo') == null ||
      (fileContainsIssuedDate && getValues('issuedDateFrom') == null) ||
      (fileContainsIssuedDate && getValues('issuedDateTo') == null) ||
      (fileContainsIssuedDate && getValues('issuedDateFormat') === null)

  const isSaveDisabled =
    !!Object.keys(errors)?.length ||
    !Object.keys(touchedFields)?.length ||
    getValues('sampleFile') === '' ||
    requiredFields

  const checkDuplicate = async (name: string) => {
    const res = await checkDuplicateFileMappingFormats(name)
    return res?.data
  }

  const onSubmit = async (rawData: any) => {
    const data: { [key: string]: any } = {
      accountNumber: rawData?.accountNumber,
      accountNumberRange:
        rawData.accountNumberFrom && rawData.accountNumberTo
          ? `${rawData.accountNumberFrom}-${rawData.accountNumberTo}`
          : '',

      amount: isTxt ? '0' : rawData?.amount,
      amountRange: rawData.amountFrom && rawData.amountTo ? `${rawData.amountFrom}-${rawData.amountTo}` : '',

      checkNumber: isTxt ? '0' : rawData?.checkNumber,
      checkNumberRange:
        rawData.checkNumberFrom && rawData.checkNumberTo ? `${rawData.checkNumberFrom}-${rawData.checkNumberTo}` : '',

      issuedDate: isTxt ? '0' : rawData?.issuedDate,
      issuedDateRange:
        rawData.issuedDateFrom && rawData.issuedDateTo ? `${rawData.issuedDateFrom}-${rawData.issuedDateTo}` : '',

      issuedPayee: rawData?.issuedPayee,
      issuedPayeeRange:
        rawData.issuedPayeeFrom && rawData.issuedPayeeTo ? `${rawData.issuedPayeeFrom}-${rawData.issuedPayeeTo}` : '',

      recordType: rawData?.recordType,
      recordTypeRange:
        rawData.recordTypeFrom && rawData.recordTypeTo ? `${rawData.recordTypeFrom}-${rawData.recordTypeTo}` : '',

      dateInFileDontIncludeSeparators: rawData.dateInFileDontIncludeSeparators,
      fileContainsHeaderRow: rawData?.fileContainsHeaderRow,
      fileContainsIssuedDate: rawData?.fileContainsIssuedDate,
      formatName: rawData?.formatName,
      formatType: rawData?.formatType,
      rowsToSkipAtEnd: 0,
      rowsToSkipAtStart: 0,
      skipRowsFrom: rawData?.skipRowsFrom,
      skipRowsTo: rawData?.skipRowsTo,
      sampleFile: rawData?.sampleFile,
      textQualifier: rawData?.textQualifier,
      issuedDateFormat: rawData?.issuedDateFormat,
    }

    if (btnSaveRef.current) {
      btnSaveRef.current.disabled = true
    }
    try {
      const isDuplicated = await checkDuplicate(data?.formatName)

      if (isDuplicated) {
        setError('formatName', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FORMAT_NAME_UNIQUE) })
        return
      }

      setIsLoading(true)

      const formData: any = new FormData()

      Object.keys(data).forEach((key: any) => {
        if (key === 'sampleFile') formData.append(key, file)
        else if (
          ['checkNumber', 'amount', 'issuedDate', 'accountNumber', 'issuedPayee', 'recordType']?.includes(key) &&
          (getValues(key) !== null || isTxt)
        ) {
          const textValue = columnList?.find((item: any) => item?.value === Number(getValues(key)))?.label
          formData.append(key, isTxt ? '0' : textValue)
        } else if (
          ['issuedPayeeRange', 'issuedDateRange', 'checkNumberRange', 'amountRange', 'accountNumberRange', 'recordTypeRange']?.includes(
            key,
          ) &&
          (getValues(key) !== null || isTxt)
        ) {
          formData.append(key, data[key])
        } else if (key !== 'dateInFileDontIncludeSeparators' && getValues(key) !== null && getValues(key) !== undefined)
          formData.append(key, data[key as keyof typeof data])
      })

      const generateColumnNumber = (field: string) => {
        const raw = getValues(field)
        if (raw === null || raw === undefined) return null
        const n = Number(raw)
        return Number.isFinite(n) ? n : null
      }

      if (!isTxt) {
        formData?.append('CheckNumberColumn', generateColumnNumber('checkNumber'))
        formData?.append('AmountColumn', generateColumnNumber('amount'))
        formData?.append('IssuedDateColumn', generateColumnNumber('issuedDate'))
        if (getValues('accountNumber') != null)
          formData?.append('AccountNumberColumn', generateColumnNumber('accountNumber'))
        if (getValues('issuedPayee') != null) formData?.append('IssuedPayeeColumn', generateColumnNumber('issuedPayee'))
        if (getValues('recordType') != null) formData?.append('RecordTypeColumn', generateColumnNumber('recordType'))
      } else {
        formData?.append('AccountNumberColumn', '0')
        formData?.append('IssuedPayeeColumn', '0')

        formData?.append('IssuedDateColumn', '0')
        formData?.append('accountNumberColumn', '0')
        formData?.append('IssuedPayeeColumn', '0')
        formData?.append('RecordTypeColumn', '0')
      }

      const res = await createFileMappingFormats(formData)
      if (res?.statusCode === 'Success') {
        navigate(menuUrls.fileMappingSetup)

        notification.success({
          message: (
            <RichTextField
              richText={t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FILE_MAPPING_CREATED_SUCCESSFULLY, {
                formatName: getValues('formatName'),
              })}
            />
          ),

          closeIcon: null,
        })
      }

      if (btnSaveRef.current) {
        btnSaveRef.current.disabled = false
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClickCancel = () => {
    navigate(menuUrls.fileMappingSetup)
  }

  useEffect(() => {
    if (['csv'].includes(formatType) && !getValues('sampleFile')) {
      setValue('textQualifier', lookupValues?.TextQualifier?.[0]?.value)
    } else {
      setValue('textQualifier', null)
    }
  }, [formatType])

  const [form] = Form.useForm()

  return (
    <Form
      form={form}
      name='file-mapping'
      className='file-mapping-setup-form-container'
      layout='vertical'
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex className='file-mapping-setup-form-wrapper' style={{ flexDirection: 'column' }}>
        <UploadForm
          errors={errors}
          control={control}
          setValue={setValue}
          trigger={trigger}
          clearErrors={clearErrors}
          onSkipRowsChange={handleSkipRowsChange}
          beforeUpload={beforeUpload}
          file={file}
          formatType={formatType}
          resetUploadFile={resetUploadFile}
        />

        {!viewNewUploadError && (
          <>
            {file?.name && (
              <>
                {props.children}
                {isTxt ? (
                  <MappingFieldsTxt
                    errors={errors}
                    control={control}
                    dirtyFields={dirtyFields}
                    fileContainsIssuedDate={fileContainsIssuedDate}
                    dateInFileDontIncludeSeparators={dateInFileDontIncludeSeparators}
                    trigger={trigger}
                    getValues={getValues}
                    resetMappingFields={resetMappingFields}
                  />
                ) : (
                  <MappingFields
                    errors={errors}
                    control={control}
                    dirtyFields={dirtyFields}
                    fileContainsIssuedDate={fileContainsIssuedDate}
                    dateInFileDontIncludeSeparators={dateInFileDontIncludeSeparators}
                    columnList={columnList}
                    getValues={getValues}
                    resetMappingFields={resetMappingFields}
                    selectedColumns={selectedColumns}
                    setSelectedColumns={setSelectedColumns}
                  />
                )}
              </>
            )}
          </>
        )}
      </Flex>

      <div className='button-container'>
        <Flex className='button-group' justify='flex-end' gap={24}>
          <Button className='cancel-button' onClick={handleClickCancel}>
            {t('OverallFunctionality:cancel_btn')}
          </Button>

          <Button
            ref={btnSaveRef}
            className='save-button'
            type='primary'
            disabled={isSaveDisabled}
            htmlType='submit'
            // onClick={form.submit}
            loading={isLoading}
          >
            {t('OverallFunctionality:save_changes_btn')}
          </Button>
        </Flex>
      </div>
    </Form>
  )
}

export default AddFileMappingWrapper

